import { UiRadiusType } from '../operator-configuration/operator-configuration.model';

export interface OperatorTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  uiRadiusType: UiRadiusType;
  headingFont: string;
  bodyFont: string;
  primaryLogoUrl?: string | null;
  logoDarkBgUrl?: string | null;
  faviconUrl?: string | null;
  heroBannerUrl?: string | null;
}

const FONT_FAMILIES: Record<string, string> = {
  inter: 'Inter, sans-serif',
  roboto: 'Roboto, sans-serif',
  'open-sans': '"Open Sans", sans-serif',
  lato: 'Lato, sans-serif',
  poppins: 'Poppins, sans-serif',
  montserrat: 'Montserrat, sans-serif',
};

export function toFontFamily(font: string): string {
  return FONT_FAMILIES[font] ?? FONT_FAMILIES['inter'];
}

export function radiusValues(uiRadiusType: UiRadiusType): {
  sm: string;
  md: string;
} {
  switch (uiRadiusType) {
    case 'sharp':
      return { sm: '0', md: '0' };
    case 'pill':
      return { sm: '999px', md: '16px' };
    default:
      return { sm: '8px', md: '12px' };
  }
}

export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '').trim();

  if (normalized.length !== 6) {
    return `rgba(211, 47, 47, ${alpha})`;
  }

  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function applyOperatorTheme(theme: OperatorTheme): void {
  const root = document.documentElement;
  const radii = radiusValues(theme.uiRadiusType);

  root.style.setProperty('--op-primary', theme.primaryColor);
  root.style.setProperty('--op-primary-light', hexToRgba(theme.primaryColor, 0.12));
  root.style.setProperty('--op-secondary', theme.secondaryColor);
  root.style.setProperty('--op-accent', theme.accentColor);
  root.style.setProperty('--op-background', theme.backgroundColor);
  root.style.setProperty('--op-radius-sm', radii.sm);
  root.style.setProperty('--op-radius-md', radii.md);
  root.style.setProperty('--op-font-body', toFontFamily(theme.bodyFont));
  root.style.setProperty('--op-font-heading', toFontFamily(theme.headingFont));

  document.body.style.fontFamily = toFontFamily(theme.bodyFont);
  document.body.style.background = theme.backgroundColor;

  if (theme.faviconUrl) {
    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }

    favicon.href = theme.faviconUrl;
  }
}
